import { PlanType } from '@labrav/react-components'
import { camelize } from 'casing'
import { getDiscountedPriceValue } from '../../../../common/utils/getDiscountedPriceValue'
import { isNonEmptyNumber } from '../../../../common/utils/validateMinMax'
import {
  AzurePlanDimension,
  AzurePlanDimensionMeter,
  Product,
  ProductPlan,
} from '../../../modules/productsListing/reducer'
import {
  AdditionalMeter,
  AzureOfferPlan,
  AzurePOPricing,
  AzurePriceInputOption,
  AzurePricingType,
  AzureRecurrentPriceField,
  BillingTerm,
  CustomMeters,
  OfferRecipients,
  OfferRecurrentPricingType,
  OfferStakeholders,
  PaymentOption,
  RecurrentPriceModeType,
} from '../../../modules/privateOffer/reducer'

type AzurePlanMeter = AzurePlanDimensionMeter & Record<string, unknown>

type ContainerFormMeter = Partial<AdditionalMeter> & {
  meterType?: string
  cloudDimensionId?: string
  cloud_dimension_id?: string
}

type AzureMeterValue =
  | AdditionalMeter
  | ContainerFormMeter
  | AzurePlanDimensionMeter
  | Record<string, unknown>

type AzureOfferPlanPricing = Pick<AzureOfferPlan, 'pricing'>

const asAzureOfferPlanFromPricing = (
  pricing?: AzurePOPricing
): AzureOfferPlan | undefined =>
  pricing ? ({ pricing } as AzureOfferPlan) : undefined

const getMeterCloudDimensionId = (meter: AzurePlanMeter) =>
  (meter.cloudDimensionId as string) ||
  (meter.cloud_dimension_id as string) ||
  (meter.meteringDimensionName as string) ||
  (meter.metering_dimension_name as string) ||
  (meter.name as string) ||
  ''

export const isAzureContainerProduct = (product?: {
  productType?: PlanType | string
}) => {
  const productType = product?.productType
  return (
    productType === PlanType.CONTAINER ||
    String(productType ?? '').toUpperCase() === 'CONTAINER'
  )
}

export const normalizeAzurePricingType = (
  pricingType?: string | null
): AzurePricingType | '' => {
  if (!pricingType) {
    return ''
  }

  const normalized = String(pricingType).toLowerCase()

  if (
    normalized === AzurePricingType.LABRA_PERCENTAGE ||
    normalized === 'percentage'
  ) {
    return AzurePricingType.LABRA_PERCENTAGE
  }

  if (normalized === AzurePricingType.ABSOLUTE) {
    return AzurePricingType.ABSOLUTE
  }

  return ''
}

export const resolveContainerLabraPlanId = ({
  apiLabraPlanId,
  productPlan,
  product,
}: {
  apiLabraPlanId?: string
  productPlan?: ProductPlan
  product?: { plans?: ProductPlan[] }
}) => {
  if (productPlan?.planId) {
    return productPlan.planId
  }

  if (!apiLabraPlanId) {
    return ''
  }

  const matchingPlan = product?.plans?.find(
    plan =>
      plan.planId === apiLabraPlanId || plan.displayName === apiLabraPlanId
  )

  return matchingPlan?.planId || apiLabraPlanId
}

const withResolvedContainerPlanIdentity = ({
  mappedPlan,
  productPlan,
  product,
}: {
  mappedPlan: Record<string, unknown>
  productPlan?: ProductPlan
  product?: Product
}) => {
  const apiLabraPlanId =
    (mappedPlan.labraPlanId as string) ||
    (mappedPlan.labra_plan_id as string) ||
    ''

  const labraPlanId = resolveContainerLabraPlanId({
    apiLabraPlanId,
    productPlan,
    product,
  })

  return {
    ...mappedPlan,
    labraPlanId,
    planId: labraPlanId || (mappedPlan.planId as string),
    displayName:
      productPlan?.displayName ||
      (mappedPlan.displayName as string) ||
      (mappedPlan.display_name as string),
    description: productPlan?.description || (mappedPlan.description as string),
  }
}

export const hasAzureContainerTcvPricing = (
  plan?: AzureOfferPlan,
  product?: { productType?: PlanType | string }
) => {
  if (!isAzureContainerProduct(product)) {
    return false
  }

  if (plan?.pricing?.recurrentPrice?.prices?.[0]) {
    return true
  }

  const pricingRecord = plan?.pricing as Record<string, unknown> | undefined
  if (pricingRecord?.systemMeterPricing) {
    return true
  }

  return hasAzureContainerSystemMeters(plan, product)
}

export const isAzureCustomMeter = (meter?: AzureMeterValue) =>
  getAzureMeterType(meter)?.toUpperCase() === 'CUSTOM'

export const isAzureSystemMeter = (meter?: AzureMeterValue) =>
  getAzureMeterType(meter)?.toUpperCase() === 'SYSTEM'

export const getMeterDisplayName = (
  meter?: AzureMeterValue,
  meterKey?: string
) => {
  const meterRecord = (meter || {}) as Record<string, unknown>

  const displayName =
    meterRecord.meteringDimensionName ||
    meterRecord.metering_dimension_name ||
    meterRecord.name ||
    meterRecord.label ||
    meterKey ||
    ''

  return String(displayName).trim()
}

export const getMeterPricePerPaymentInUsd = (meter?: AzureMeterValue) => {
  if (!meter) {
    return undefined
  }

  const meterRecord = meter as Record<string, unknown>
  const price =
    meterRecord.pricePerPaymentInUsd ?? meterRecord.price_per_payment_in_usd

  return typeof price === 'number' ? price : undefined
}

export const getContainerTcvFromMeters = (
  meters?: Record<string, AzureMeterValue>
) => {
  if (!meters || Object.keys(meters).length === 0) {
    return undefined
  }

  const systemMeter = Object.values(meters).find(meter =>
    isAzureSystemMeter(meter)
  )
  const systemMeterPrice = getMeterPricePerPaymentInUsd(systemMeter)
  if (isNonEmptyNumber(systemMeterPrice)) {
    return systemMeterPrice
  }

  const nonCustomMeter = Object.values(meters).find(
    meter => !isAzureCustomMeter(meter)
  )
  const nonCustomMeterPrice = getMeterPricePerPaymentInUsd(nonCustomMeter)
  if (isNonEmptyNumber(nonCustomMeterPrice)) {
    return nonCustomMeterPrice
  }

  return undefined
}

export const getContainerTcvFromProductPlan = (productPlan: ProductPlan) => {
  const productDimensions = productPlan?.dimensions as
    | AzurePlanDimension
    | undefined

  return getContainerTcvFromMeters(
    Object.fromEntries(
      normalizeAzurePlanMeters(productDimensions?.meters).map(meter => [
        meter.cloudDimensionId,
        meter as unknown as ContainerFormMeter,
      ])
    )
  )
}

export const getContainerPlanTcvValue = (plan?: AzureOfferPlan) => {
  const pricingRecord = plan?.pricing as Record<string, unknown> | undefined
  const systemMeterPricing = pricingRecord?.systemMeterPricing as
    | { price?: number }
    | undefined

  const systemMeterPrice = systemMeterPricing?.price
  if (isNonEmptyNumber(systemMeterPrice)) {
    return systemMeterPrice
  }

  const priceFromRecurrentPrice = plan?.pricing?.recurrentPrice?.prices?.[0] as
    | Record<string, unknown>
    | undefined

  if (priceFromRecurrentPrice) {
    const recurrentPrice =
      priceFromRecurrentPrice.pricePerPaymentInUsd ??
      priceFromRecurrentPrice.price_per_payment_in_usd

    if (typeof recurrentPrice === 'number') {
      return recurrentPrice
    }
  }

  return getContainerTcvFromMeters(plan?.pricing?.customMeters?.meters)
}

export const getAzureMeterType = (
  meter?: AzureMeterValue
): string | undefined => {
  if (!meter) {
    return undefined
  }

  const meterRecord = meter as Record<string, unknown>
  const meterType = meterRecord.meterType || meterRecord.meter_type
  return meterType ? String(meterType) : undefined
}

export const hasCustomMeteringDimensions = (plan?: AzureOfferPlanPricing) => {
  const meters = plan?.pricing?.customMeters?.meters
  if (!meters || Object.keys(meters).length === 0) {
    return false
  }

  return Object.values(meters).some(meter => isAzureCustomMeter(meter))
}

export const shouldShowContainerTcv = (
  plan?: AzureOfferPlan,
  product?: { productType?: PlanType | string }
) =>
  hasAzureContainerTcvPricing(plan, product) &&
  !hasCustomMeteringDimensions(plan)

export const getContainerSystemMeterPriceTitle = (
  _plan?: AzureOfferPlan,
  azurePricingType?: AzurePricingType | string
) =>
  azurePricingType === AzurePricingType.LABRA_PERCENTAGE
    ? 'Discount price per hour'
    : 'Price per hour'

export const shouldSkipContainerTcvValidation = (plan?: AzureOfferPlan) => {
  if (!hasCustomMeteringDimensions(plan)) {
    return false
  }

  const billingTerm = plan?.pricing?.recurrentPrice?.prices?.[0]?.billingTerm
  return (
    !billingTerm?.type &&
    !isNonEmptyNumber(billingTerm?.value as number | undefined)
  )
}

export const getContainerCustomMeters = (plan?: AzureOfferPlan) =>
  Object.fromEntries(
    Object.entries(plan?.pricing?.customMeters?.meters || {}).filter(
      ([, meter]) => isAzureCustomMeter(meter)
    )
  )

export const hasAzureContainerSystemMeters = (
  plan?: AzureOfferPlan,
  product?: { productType?: PlanType | string }
) => {
  if (!isAzureContainerProduct(product)) {
    return false
  }

  const meters = plan?.pricing?.customMeters?.meters
  if (!meters || Object.keys(meters).length === 0) {
    return false
  }

  return Object.values(meters).some(
    meter => getAzureMeterType(meter)?.toUpperCase() === 'SYSTEM'
  )
}

export const getAzureMetersByType = (
  plan?: AzureOfferPlan,
  meterType?: string
) => {
  const meters = plan?.pricing?.customMeters?.meters
  if (!meters || !meterType) {
    return {}
  }

  const normalizedType = meterType.toUpperCase()
  return Object.fromEntries(
    Object.entries(meters).filter(
      ([, meter]) => getAzureMeterType(meter)?.toUpperCase() === normalizedType
    )
  )
}

const normalizeIncludedQuantities = (
  includedQuantities?: AzurePlanDimensionMeter['includedQuantities']
) =>
  (includedQuantities || []).map(quantityItem => {
    const quantityRecord = quantityItem as unknown as Record<string, unknown>
    return {
      ...quantityItem,
      billingTerm:
        quantityItem.billingTerm ||
        (quantityRecord.billing_term as AzurePlanDimensionMeter['includedQuantities'][0]['billingTerm']),
      isInfinite:
        quantityItem.isInfinite ??
        (quantityRecord.is_infinite as boolean | undefined),
      quantity: quantityItem.quantity,
    }
  })

export const normalizeAzurePlanMeters = (
  meters: AzurePlanDimension['meters'] | Record<string, unknown> | undefined
): AzurePlanDimensionMeter[] => {
  if (!meters) {
    return []
  }

  if (Array.isArray(meters)) {
    return meters.map(meter => {
      const meterData = meter as AzurePlanMeter
      return {
        ...meterData,
        cloudDimensionId: getMeterCloudDimensionId(meterData),
        meteringDimensionName:
          meterData.meteringDimensionName ||
          (meterData.metering_dimension_name as string) ||
          (meterData.name as string),
        includedQuantities: normalizeIncludedQuantities(
          meterData.includedQuantities ||
            (meterData.included_quantities as AzurePlanDimensionMeter['includedQuantities'])
        ),
        pricePerPaymentInUsd:
          meterData.pricePerPaymentInUsd ??
          (meterData.price_per_payment_in_usd as number),
        unitOfMeasure:
          meterData.unitOfMeasure || (meterData.unit_of_measure as string),
        meterType: meterData.meterType || (meterData.meter_type as string),
      }
    })
  }

  if (typeof meters === 'object') {
    return Object.entries(meters).map(([key, meter]) => {
      const meterData = meter as AzurePlanMeter
      return {
        ...meterData,
        cloudDimensionId: getMeterCloudDimensionId(meterData) || key,
        meteringDimensionName:
          meterData.meteringDimensionName ||
          (meterData.metering_dimension_name as string) ||
          (meterData.name as string),
        includedQuantities: normalizeIncludedQuantities(
          meterData.includedQuantities ||
            (meterData.included_quantities as AzurePlanDimensionMeter['includedQuantities'])
        ),
        pricePerPaymentInUsd:
          meterData.pricePerPaymentInUsd ??
          (meterData.price_per_payment_in_usd as number),
        unitOfMeasure:
          meterData.unitOfMeasure || (meterData.unit_of_measure as string),
        meterType: meterData.meterType || (meterData.meter_type as string),
      }
    })
  }

  return []
}

export const getContainerPlanMeters = ({
  productPlan,
  pricesValue,
}: {
  productPlan: ProductPlan
  pricesValue?: AzureRecurrentPriceField[]
}) => {
  let customMetersObject: Record<string, ContainerFormMeter> = {}

  const productDimensions = productPlan?.dimensions as
    | AzurePlanDimension
    | undefined
  const productsMeters = normalizeAzurePlanMeters(productDimensions?.meters)

  productsMeters.forEach(
    ({ cloudDimensionId, includedQuantities, ...remaingProductsMeterData }) => {
      if (!cloudDimensionId) {
        return
      }

      customMetersObject = {
        ...customMetersObject,
        [cloudDimensionId]: {
          ...remaingProductsMeterData,
          meteringDimensionName: getMeterDisplayName(
            remaingProductsMeterData,
            cloudDimensionId
          ),
          includedQuantities: pricesValue?.length
            ? includedQuantities?.filter(
                item =>
                  item?.billingTerm?.type ===
                    pricesValue[0]?.billingTerm?.type &&
                  item?.billingTerm?.value ===
                    pricesValue[0]?.billingTerm?.value
              )
            : isAzureCustomMeter(remaingProductsMeterData)
              ? []
              : includedQuantities,
        },
      }
    }
  )

  return customMetersObject
}

const normalizeBillingTerm = (
  billingTerm?: BillingTerm | Record<string, unknown>
): BillingTerm | undefined => {
  if (!billingTerm || typeof billingTerm !== 'object') {
    return undefined
  }

  const billingTermRecord = billingTerm as Record<string, unknown>
  const type = billingTermRecord.type
  const value = billingTermRecord.value

  if (!type || !isNonEmptyNumber(value as number)) {
    return undefined
  }

  return {
    type: String(type),
    value: Number(value),
  }
}

const normalizePaymentOption = (
  paymentOption?: PaymentOption | Record<string, unknown>,
  billingTerm?: BillingTerm
): PaymentOption | undefined => {
  if (paymentOption && typeof paymentOption === 'object') {
    const paymentOptionRecord = paymentOption as Record<string, unknown>
    const type = paymentOptionRecord.type
    const value = paymentOptionRecord.value

    if (type && isNonEmptyNumber(value as number)) {
      return {
        type: String(type),
        value: Number(value),
      }
    }
  }

  if (billingTerm) {
    return {
      type: billingTerm.type,
      value: billingTerm.value,
    }
  }

  return undefined
}

export const getContainerPlanPriceDefaults = (
  productPlan: ProductPlan
): Pick<AzureRecurrentPriceField, 'billingTerm' | 'paymentOption'> => {
  const planPricing = productPlan?.pricing
  if (Array.isArray(planPricing) && planPricing[0]) {
    const firstPrice = planPricing[0] as unknown as Record<string, unknown>
    const billingTerm = normalizeBillingTerm(
      (firstPrice.billingTerm ?? firstPrice.billing_term) as BillingTerm
    )
    const paymentOption = normalizePaymentOption(
      (firstPrice.paymentOption ?? firstPrice.payment_option) as PaymentOption,
      billingTerm
    )

    if (billingTerm && paymentOption) {
      return { billingTerm, paymentOption }
    }
  }

  const productDimensions = productPlan?.dimensions as
    | AzurePlanDimension
    | undefined
  const meters = normalizeAzurePlanMeters(productDimensions?.meters)

  for (const meter of meters) {
    const billingTerm = normalizeBillingTerm(
      meter.includedQuantities?.[0]?.billingTerm
    )
    const paymentOption = normalizePaymentOption(undefined, billingTerm)

    if (billingTerm && paymentOption) {
      return { billingTerm, paymentOption }
    }
  }

  return {
    billingTerm: { type: 'month', value: 1 },
    paymentOption: { type: 'month', value: 1 },
  }
}

export const buildContainerRecurrentPriceField = (
  productPlan: ProductPlan,
  pricePerPaymentInUsd?: number
): AzureRecurrentPriceField => {
  const { billingTerm, paymentOption } =
    getContainerPlanPriceDefaults(productPlan)

  return {
    billingTerm,
    paymentOption,
    pricePerPaymentInUsd,
  }
}

export const enrichContainerPlanRecurrentPrices = (
  prices: AzureRecurrentPriceField[] | undefined,
  productPlan?: ProductPlan
): AzureRecurrentPriceField[] => {
  if (!prices?.length) {
    return prices || []
  }

  const defaults = productPlan
    ? getContainerPlanPriceDefaults(productPlan)
    : {
        billingTerm: { type: 'month', value: 1 },
        paymentOption: { type: 'month', value: 1 },
      }

  return prices.map(price => ({
    ...price,
    billingTerm: price.billingTerm ?? defaults.billingTerm,
    paymentOption: price.paymentOption ?? defaults.paymentOption,
  }))
}

export const shouldSendContainerRecurrentPrices = (
  plan?: AzureOfferPlanPricing
) => {
  const firstPrice = plan?.pricing?.recurrentPrice?.prices?.[0]
  const hasTcv = isNonEmptyNumber(
    firstPrice?.pricePerPaymentInUsd ??
      getContainerPlanTcvValue(asAzureOfferPlanFromPricing(plan?.pricing))
  )
  const hasInstallments = (firstPrice?.installments?.schedule?.length ?? 0) > 0

  if (hasTcv || hasInstallments) {
    return true
  }

  return !hasCustomMeteringDimensions(plan)
}

export const resolveContainerRecurrentPricesForPayload = (
  prices: AzureRecurrentPriceField[] | undefined,
  plan: AzureOfferPlanPricing,
  productPlan?: ProductPlan
) => {
  if (!shouldSendContainerRecurrentPrices(plan)) {
    return []
  }

  return enrichContainerPlanRecurrentPrices(prices, productPlan)
}

export const getInitialContainerPlanPrices = (
  productPlan: ProductPlan
): AzureRecurrentPriceField[] => {
  const planPricing = productPlan?.pricing
  if (Array.isArray(planPricing) && planPricing[0]) {
    const firstPrice = planPricing[0] as unknown as Record<string, unknown>
    const pricePerPaymentInUsd =
      firstPrice.pricePerPaymentInUsd ?? firstPrice.price_per_payment_in_usd

    if (!isNonEmptyNumber(pricePerPaymentInUsd as number)) {
      const tcvFromMeters = getContainerTcvFromProductPlan(productPlan)
      if (!isNonEmptyNumber(tcvFromMeters)) {
        return []
      }

      return [buildContainerRecurrentPriceField(productPlan, tcvFromMeters)]
    }

    return [
      buildContainerRecurrentPriceField(
        productPlan,
        pricePerPaymentInUsd as number
      ),
    ]
  }

  const tcvFromMeters = getContainerTcvFromProductPlan(productPlan)
  if (!isNonEmptyNumber(tcvFromMeters)) {
    return []
  }

  return [buildContainerRecurrentPriceField(productPlan, tcvFromMeters)]
}

export const isContainerDiscountPricingType = (
  azurePricingType?: AzurePricingType | string
) =>
  azurePricingType === AzurePricingType.LABRA_PERCENTAGE ||
  String(azurePricingType ?? '').toLowerCase() === 'percentage'

export const mapContainerDiscountTypeForPayload = (
  azurePricingType?: AzurePricingType | string
) =>
  isContainerDiscountPricingType(azurePricingType)
    ? 'percentage'
    : azurePricingType

const resolveContainerDiscountPrice = ({
  currentPrice,
  originalPrice,
  discountPercentage,
  isDiscounted,
}: {
  currentPrice?: number | null
  originalPrice?: number | null
  discountPercentage?: number | null
  isDiscounted: boolean
}) => {
  const resolvedOriginalPrice = originalPrice ?? undefined
  const resolvedDiscount = discountPercentage ?? undefined
  const resolvedCurrentPrice = currentPrice ?? undefined

  if (
    isDiscounted &&
    isNonEmptyNumber(resolvedOriginalPrice) &&
    isNonEmptyNumber(resolvedDiscount)
  ) {
    return getDiscountedPriceValue({
      price: resolvedOriginalPrice as number,
      discount: resolvedDiscount as number,
    })
  }

  if (isNonEmptyNumber(resolvedCurrentPrice)) {
    return resolvedCurrentPrice
  }

  return resolvedCurrentPrice
}

export const buildContainerPlanPayloadPricing = ({
  pricing,
  originalPricing,
  azurePricingType,
  discountPercentage,
}: {
  pricing?: AzurePOPricing
  originalPricing?: AzurePOPricing
  azurePricingType?: AzurePricingType | string
  discountPercentage?: number | null
}) => {
  const plan = { pricing } as AzureOfferPlan
  const isDiscounted = isContainerDiscountPricingType(azurePricingType)

  if (hasCustomMeteringDimensions(plan)) {
    const customMeters = getContainerCustomMeters(plan)

    return {
      customMeters: {
        priceInputOption: AzurePriceInputOption.USD,
        meters: Object.entries(customMeters).map(([meterKey, meter]) => {
          const meterRecord = meter as ContainerFormMeter
          const originalMeter =
            originalPricing?.customMeters?.meters?.[meterKey]
          const meterPrice = getMeterPricePerPaymentInUsd(meterRecord)
          const originalPrice =
            getMeterPricePerPaymentInUsd(originalMeter) ?? meterPrice
          const pricePerPaymentInUsd = resolveContainerDiscountPrice({
            currentPrice: meterPrice,
            originalPrice,
            discountPercentage,
            isDiscounted,
          })

          const includedQuantities = (meterRecord.includedQuantities || [])
            .filter(
              quantityItem =>
                !quantityItem.isInfinite &&
                isNonEmptyNumber(quantityItem.quantity)
            )
            .map(quantityItem => ({
              ...(quantityItem.billingTerm
                ? { billingTerm: quantityItem.billingTerm }
                : {}),
              isInfinite: quantityItem.isInfinite,
              quantity: quantityItem.quantity,
            }))

          return {
            cloudDimensionId:
              meterRecord.cloudDimensionId ||
              meterRecord.cloud_dimension_id ||
              meterKey,
            unitOfMeasure: meterRecord.unitOfMeasure,
            meteringDimensionName: getMeterDisplayName(meterRecord, meterKey),
            includedQuantities,
            pricePerPaymentInUsd,
          }
        }),
      },
    }
  }

  let systemMeters = getAzureMetersByType(plan, 'SYSTEM')
  let [systemMeterKey, systemMeter] = Object.entries(systemMeters)[0] || []

  if (!systemMeter && isDiscounted && originalPricing) {
    systemMeters = getAzureMetersByType(
      asAzureOfferPlanFromPricing(originalPricing),
      'SYSTEM'
    )
    ;[systemMeterKey, systemMeter] = Object.entries(systemMeters)[0] || []
  }

  if (systemMeter) {
    const price =
      getContainerPlanTcvValue(plan) ??
      getMeterPricePerPaymentInUsd(systemMeter)
    const originalPrice =
      getContainerPlanTcvValue(asAzureOfferPlanFromPricing(originalPricing)) ??
      getMeterPricePerPaymentInUsd(
        originalPricing?.customMeters?.meters?.[systemMeterKey]
      ) ??
      price
    const resolvedPrice = resolveContainerDiscountPrice({
      currentPrice: price,
      originalPrice,
      discountPercentage,
      isDiscounted,
    })

    const systemMeterRecord = systemMeter as unknown as Record<string, unknown>
    const priceInputOption =
      systemMeter.unitOfMeasure ||
      (systemMeterRecord.unit_of_measure as string) ||
      AzurePriceInputOption.USD

    return {
      systemMeterPricing: {
        priceInputOption,
        price: resolvedPrice,
      },
    }
  }

  return {}
}

const filterOfferRecipients = (offerRecipients: OfferRecipients[] = []) =>
  offerRecipients.filter(
    offerRecipientItem =>
      String(offerRecipientItem.firstName || '').trim() !== '' ||
      String(offerRecipientItem.lastName || '').trim() !== '' ||
      String(offerRecipientItem.email || '').trim() !== '' ||
      String(offerRecipientItem.title || '').trim() !== ''
  )

export const buildAzureOfferStakeholdersForPayload = ({
  crmOpportunityId,
  offerStakeholders,
  customOfferStakeholders,
}: {
  crmOpportunityId?: string
  offerStakeholders?: OfferStakeholders[]
  customOfferStakeholders?: OfferStakeholders[]
}) => {
  if (crmOpportunityId) {
    return []
  }

  return (offerStakeholders || []).concat(
    (customOfferStakeholders || []).map(({ email }) => ({
      email,
      firstName: '',
      lastName: '',
    }))
  )
}

export const buildContainerPlanPayloadItem = ({
  plan,
  azurePricingType,
}: {
  plan: AzureOfferPlan
  azurePricingType?: AzurePricingType | string
}) => {
  const {
    originalPricing,
    pricing,
    discountPercentage,
    displayName,
    labraPlanId,
  } = plan

  const discountType = mapContainerDiscountTypeForPayload(azurePricingType)
  const planPricing = buildContainerPlanPayloadPricing({
    pricing,
    originalPricing,
    azurePricingType,
    discountPercentage,
  })

  if (isContainerDiscountPricingType(azurePricingType)) {
    return {
      displayName,
      labraPlanId,
      discountType,
      discountPercentage,
      pricing: planPricing,
    }
  }

  return {
    displayName,
    labraPlanId,
    discountType,
    pricing: planPricing,
  }
}

export const buildAzureContainerPrivateOfferPayload = ({
  privateOfferName,
  crmOrderObjectId,
  crmOpportunityId,
  cloudProductId,
  billingAccountId,
  variableStartDate,
  existingContractRenewal,
  offerExpirationDate,
  acceptBy,
  preparedBy,
  eulaType,
  offerDescription,
  companyName,
  emailCustomMessage,
  offerRecipients,
  offerStakeholders,
  customOfferStakeholders,
  plans,
  azurePricingType,
  offerStartDate,
}: {
  privateOfferName: string
  crmOrderObjectId?: string
  crmOpportunityId?: string
  cloudProductId?: string
  billingAccountId?: string
  variableStartDate?: boolean
  existingContractRenewal?: boolean
  offerExpirationDate?: string | null
  acceptBy?: string | null
  preparedBy?: string
  eulaType?: string
  offerDescription?: string | null
  companyName?: string | null
  emailCustomMessage?: string | null
  offerRecipients?: OfferRecipients[]
  offerStakeholders?: OfferStakeholders[]
  customOfferStakeholders?: OfferStakeholders[]
  plans?: AzureOfferPlan[]
  azurePricingType?: AzurePricingType | string
  offerStartDate?: string | null
}) => ({
  name: privateOfferName,
  crmOrderObjectId: crmOrderObjectId || crmOpportunityId,
  cloudProductId,
  billingAccountId,
  privateOfferType: 'customerPromotion',
  ...(variableStartDate !== undefined ? { variableStartDate } : {}),
  existingContractRenewal: existingContractRenewal ?? false,
  ...(offerExpirationDate ? { offerExpirationDate } : {}),
  ...(acceptBy ? { acceptBy } : {}),
  ...(!variableStartDate && offerStartDate ? { offerStartDate } : {}),
  ...(preparedBy ? { preparedBy } : {}),
  ...(eulaType ? { eulaType } : {}),
  ...(offerDescription ? { offerDescription } : {}),
  ...(companyName ? { companyName } : {}),
  ...(emailCustomMessage ? { emailCustomMessage } : {}),
  offerRecipients: filterOfferRecipients(offerRecipients),
  offerStakeholders: buildAzureOfferStakeholdersForPayload({
    crmOpportunityId,
    offerStakeholders,
    customOfferStakeholders,
  }),
  plans: (plans || [])
    .filter(planItem => Boolean(planItem?.labraPlanId))
    .map(planItem =>
      buildContainerPlanPayloadItem({
        plan: planItem,
        azurePricingType,
      })
    ),
})

const mapApiCustomMetersToObject = (
  meters: unknown,
  productMeters: Record<string, ContainerFormMeter>
) => {
  if (!meters) {
    return {}
  }

  const meterEntries: Array<{ key: string; meter: Record<string, unknown> }> =
    Array.isArray(meters)
      ? meters.map((meter, index) => {
          const meterRecord = (meter || {}) as Record<string, unknown>
          const key =
            (meterRecord.cloudDimensionId as string) ||
            (meterRecord.cloud_dimension_id as string) ||
            String(index)

          return { key, meter: meterRecord }
        })
      : Object.entries(meters as Record<string, unknown>).map(
          ([key, meter]) => ({
            key,
            meter: (meter || {}) as Record<string, unknown>,
          })
        )

  return meterEntries.reduce(
    (acc, { key, meter }) => {
      const productMeter = productMeters[key] || {}
      const meterType =
        getAzureMeterType(productMeter) || getAzureMeterType(meter) || 'CUSTOM'

      acc[key] = {
        ...productMeter,
        ...meter,
        cloudDimensionId: key,
        meterType,
        meteringDimensionName: getMeterDisplayName(meter, key),
        unitOfMeasure:
          (meter.unitOfMeasure as string) ||
          (meter.unit_of_measure as string) ||
          productMeter.unitOfMeasure,
        pricePerPaymentInUsd:
          getMeterPricePerPaymentInUsd(meter) ??
          productMeter.pricePerPaymentInUsd,
        includedQuantities:
          meterType.toUpperCase() === 'CUSTOM'
            ? []
            : productMeter.includedQuantities || [],
      } as ContainerFormMeter

      return acc
    },
    {} as Record<string, ContainerFormMeter>
  )
}

const buildSystemMeterMetersFromApi = ({
  productMeters,
  price,
  priceInputOption,
}: {
  productMeters: Record<string, ContainerFormMeter>
  price?: number
  priceInputOption?: string
}) => {
  const meters: Record<string, ContainerFormMeter> = { ...productMeters }
  const systemMeterEntry = Object.entries(meters).find(([, meter]) =>
    isAzureSystemMeter(meter)
  )

  if (systemMeterEntry) {
    const [key, meter] = systemMeterEntry
    meters[key] = {
      ...meter,
      pricePerPaymentInUsd: price ?? meter.pricePerPaymentInUsd,
      unitOfMeasure: priceInputOption || meter.unitOfMeasure,
    }
    return meters
  }

  const matchByUnit = Object.entries(meters).find(
    ([, meter]) => meter.unitOfMeasure === priceInputOption
  )

  if (matchByUnit) {
    const [key, meter] = matchByUnit
    meters[key] = {
      ...meter,
      meterType: 'SYSTEM',
      pricePerPaymentInUsd: price ?? meter.pricePerPaymentInUsd,
    }
    return meters
  }

  if (priceInputOption) {
    meters[priceInputOption] = {
      meterType: 'SYSTEM',
      unitOfMeasure: priceInputOption,
      pricePerPaymentInUsd: price ?? 0,
    }
  }

  return meters
}

const withContainerPlanPricingDefaults = (
  mappedPlan: Record<string, unknown>,
  offerPlanFromProduct?: Pick<
    AzureOfferPlan,
    'originalPricing' | 'discountPercentage'
  >
) => ({
  ...mappedPlan,
  ...(offerPlanFromProduct?.originalPricing
    ? { originalPricing: offerPlanFromProduct.originalPricing }
    : {}),
  discountPercentage:
    (mappedPlan.discountPercentage as number | null | undefined) ??
    offerPlanFromProduct?.discountPercentage ??
    undefined,
})

const getContainerPlanPricingDefaults = (
  offerPlan?: ReturnType<typeof convertContainerProductPlanToOfferPlan>
):
  | Pick<AzureOfferPlan, 'originalPricing' | 'discountPercentage'>
  | undefined =>
  offerPlan
    ? (offerPlan as unknown as Pick<
        AzureOfferPlan,
        'originalPricing' | 'discountPercentage'
      >)
    : undefined

export const mapContainerAzurePlanFromApi = ({
  apiPlan,
  productPlan,
  product,
}: {
  apiPlan: Record<string, unknown>
  productPlan?: ProductPlan
  product?: Product
}) => {
  const camelizedPlan = camelize(apiPlan) as Record<string, unknown>
  const pricing = (camelizedPlan.pricing || {}) as Record<string, unknown>
  const productMeters = productPlan
    ? getContainerPlanMeters({ productPlan })
    : {}
  const offerPlanFromProduct = productPlan
    ? convertContainerProductPlanToOfferPlan(productPlan)
    : undefined
  const offerPlanPricingDefaults =
    getContainerPlanPricingDefaults(offerPlanFromProduct)

  const systemMeterPricing = pricing.systemMeterPricing as
    | { price?: number; priceInputOption?: string }
    | undefined

  if (systemMeterPricing) {
    const price = systemMeterPricing.price
    const priceInputOption = systemMeterPricing.priceInputOption
    const meters = buildSystemMeterMetersFromApi({
      productMeters,
      price,
      priceInputOption,
    })

    return withResolvedContainerPlanIdentity({
      mappedPlan: withContainerPlanPricingDefaults(
        {
          ...camelizedPlan,
          pricing: {
            recurrentPrice: {
              recurrentPriceMode: RecurrentPriceModeType.FLATRATE,
              priceInputOption: AzurePriceInputOption.USD,
              prices: isNonEmptyNumber(price)
                ? [{ pricePerPaymentInUsd: price }]
                : [],
            },
            customMeters: { meters },
            systemMeterPricing,
          },
        },
        offerPlanPricingDefaults
      ),
      productPlan,
      product,
    })
  }

  const customMetersData = pricing.customMeters as
    | { meters?: unknown; priceInputOption?: string }
    | undefined

  if (customMetersData?.meters) {
    const meters = mapApiCustomMetersToObject(
      customMetersData.meters,
      productMeters
    )

    return withResolvedContainerPlanIdentity({
      mappedPlan: withContainerPlanPricingDefaults(
        {
          ...camelizedPlan,
          pricing: {
            recurrentPrice: {
              recurrentPriceMode: RecurrentPriceModeType.FLATRATE,
              priceInputOption: AzurePriceInputOption.USD,
              prices: [],
            },
            customMeters: {
              priceInputOption:
                customMetersData.priceInputOption || AzurePriceInputOption.USD,
              meters,
            },
          },
        },
        offerPlanPricingDefaults
      ),
      productPlan,
      product,
    })
  }

  if (offerPlanFromProduct?.pricing) {
    return withResolvedContainerPlanIdentity({
      mappedPlan: withContainerPlanPricingDefaults(
        {
          ...camelizedPlan,
          pricing: offerPlanFromProduct.pricing,
        },
        offerPlanPricingDefaults
      ),
      productPlan,
      product,
    })
  }

  return withResolvedContainerPlanIdentity({
    mappedPlan: withContainerPlanPricingDefaults(
      {
        ...camelizedPlan,
        pricing: {
          recurrentPrice:
            (pricing.recurrentPrice as AzurePOPricing['recurrentPrice']) || {
              recurrentPriceMode: RecurrentPriceModeType.FLATRATE,
              priceInputOption: AzurePriceInputOption.USD,
              prices: [],
            },
          customMeters: {
            meters: productMeters,
          },
        },
      },
      offerPlanPricingDefaults
    ),
    productPlan,
    product,
  })
}

export const mapContainerAzurePlansFromApi = ({
  plans,
  product,
}: {
  plans?: Array<Record<string, unknown>>
  product?: Product
}) =>
  (plans || []).map(plan => {
    const camelizedPlan = camelize(plan) as Record<string, unknown>
    const labraPlanId =
      (camelizedPlan.labraPlanId as string) ||
      (camelizedPlan.labra_plan_id as string)
    const resolvedLabraPlanId = resolveContainerLabraPlanId({
      apiLabraPlanId: labraPlanId,
      product,
    })
    const productPlan = product?.plans?.find(
      item =>
        item.planId === resolvedLabraPlanId || item.displayName === labraPlanId
    )

    return mapContainerAzurePlanFromApi({
      apiPlan: plan,
      productPlan,
      product,
    })
  })

export const convertContainerProductPlanPricingToOffer = (
  productPlan: ProductPlan
) => {
  const pricesValue = getInitialContainerPlanPrices(productPlan)

  const recurrentPriceModeValue =
    isNonEmptyNumber(productPlan?.minQuantity) &&
    isNonEmptyNumber(productPlan?.maxQuantity)
      ? RecurrentPriceModeType.PERUSER
      : RecurrentPriceModeType.FLATRATE
  const meters = { ...getContainerPlanMeters({ productPlan, pricesValue }) }

  return {
    pricingData: {
      recurrentPrice: {
        recurrentPriceMode: recurrentPriceModeValue,
        priceInputOption: AzurePriceInputOption.USD,
        userLimits: {
          min: productPlan?.minQuantity,
          max: productPlan?.maxQuantity,
          minEnable: true,
          maxEnable: true,
        },
        prices: pricesValue,
      },
      customMeters: {
        meters: { ...meters },
      },
    },
    originalPricingData: {
      recurrentPrice: {
        recurrentPriceMode: recurrentPriceModeValue,
        priceInputOption: AzurePriceInputOption.USD,
        userLimits: {
          min: productPlan?.minQuantity,
          max: productPlan?.maxQuantity,
          minEnable: true,
          maxEnable: true,
        },
        prices: getInitialContainerPlanPrices(productPlan),
      },
      customMeters: {
        meters: { ...getContainerPlanMeters({ productPlan }) },
      },
    },
  }
}

export const convertContainerProductPlanToOfferPlan = (
  productPlan?: ProductPlan
) => {
  if (!productPlan) return productPlan

  const { pricingData, originalPricingData } =
    convertContainerProductPlanPricingToOffer(productPlan)

  return {
    planId: productPlan?.planId,
    labraPlanId: productPlan.planId,
    azurePlanId: productPlan?.azurePlanId,
    description: productPlan?.description,
    displayName: productPlan?.displayName,
    offerPricingType: OfferRecurrentPricingType.EDITEXISTINGOFFERPRICINGONLY,
    pricing: pricingData,
    originalPricing: originalPricingData,
    discountPercentage: undefined,
  }
}

export const resetContainerPlansForPricingType = ({
  plans,
  product,
}: {
  plans: AzureOfferPlan[]
  product?: Product
}) =>
  plans.map(planItem => {
    if (!planItem?.labraPlanId) {
      return planItem
    }

    const productPlan = product?.plans?.find(
      item => item?.planId === planItem.labraPlanId
    )

    if (!productPlan) {
      return {
        ...planItem,
        discountPercentage: undefined,
      }
    }

    return convertContainerProductPlanToOfferPlan(productPlan) as AzureOfferPlan
  })

export const getUpdatedContainerCustomMeters = (
  plan: AzureOfferPlan,
  discountPercentage?: number,
  customMeters?: CustomMeters
) => {
  let updatedCustomMeters = customMeters
  if (customMeters?.meters && Object.keys(customMeters?.meters).length > 0) {
    const originalCustomMeters = plan.originalPricing?.customMeters?.meters
    if (originalCustomMeters) {
      const updatedMeters = { ...customMeters?.meters }

      Object.keys(updatedMeters).forEach(meterKey => {
        const meter = updatedMeters[meterKey]
        if (!isAzureCustomMeter(meter)) {
          return
        }

        const originalMeter = originalCustomMeters[meterKey]
        const originalPrice = getMeterPricePerPaymentInUsd(originalMeter)
        if (!isNonEmptyNumber(originalPrice)) {
          return
        }

        const resolvedMeterPrice = isNonEmptyNumber(discountPercentage)
          ? getDiscountedPriceValue({
              price: originalPrice as number,
              discount: discountPercentage,
            })
          : (originalPrice as number)

        updatedMeters[meterKey] = {
          ...updatedMeters[meterKey],
          pricePerPaymentInUsd: resolvedMeterPrice,
        }
      })

      updatedCustomMeters = {
        ...customMeters,
        meters: updatedMeters,
      }
    }
  }
  return updatedCustomMeters
}

export const updateContainerPlanDiscount = ({
  plan,
  product,
  discountPercentage,
}: {
  plan: AzureOfferPlan
  product: Product
  discountPercentage?: number
}) => {
  const isCustomMeterPlan = hasCustomMeteringDimensions(plan)
  const hasTcvPricing = hasAzureContainerTcvPricing(plan, product)

  if (!isCustomMeterPlan && !hasTcvPricing) {
    return null
  }

  const updatedCustomMeters = getUpdatedContainerCustomMeters(
    plan,
    discountPercentage,
    plan?.pricing?.customMeters
  )

  if (isCustomMeterPlan && !hasTcvPricing) {
    return {
      ...plan,
      discountPercentage,
      pricing: {
        ...plan.pricing,
        customMeters: updatedCustomMeters,
      },
    }
  }

  const originalPrice =
    getContainerPlanTcvValue(
      asAzureOfferPlanFromPricing(plan?.originalPricing)
    ) ??
    getContainerTcvFromMeters(plan?.originalPricing?.customMeters?.meters) ??
    0

  if (!isNonEmptyNumber(originalPrice) || originalPrice <= 0) {
    return null
  }

  const discountedTcv = getDiscountedPriceValue({
    price: originalPrice,
    discount: discountPercentage || 0,
  })

  return {
    ...plan,
    discountPercentage,
    pricing: {
      ...plan.pricing,
      recurrentPrice: {
        ...(plan.pricing?.recurrentPrice || {}),
        prices: [
          {
            ...(plan.pricing?.recurrentPrice?.prices?.[0] || {}),
            pricePerPaymentInUsd: discountedTcv,
          },
        ],
      },
      customMeters: updatedCustomMeters,
    },
  }
}
